import type { DealOrderType, ProcessingStep, SearchBargain } from '@/types/deals';
import type { NotificationItem } from '@/types/notifications';

// Чистая доменная логика дашборда (главной кабинета). Без React/JSX — покрывается юнит-тестами.
// Источник истины «чей ход» — processingStep сделки + моя сторона (вкладка списка):
// SALE → я продавец, PURCHASE → я покупатель. Шаги, не требующие моего действия
// (*_PROCESSING / *_GENERATION / экспедитор / завершённые), в ленту не попадают.

type Actor = 'SELLER' | 'BUYER' | 'AGENT';

interface StepAction {
  readonly actor: Actor;
  readonly labelKey: string;
  /** Финансовое действие (доплата/возврат) — для отдельной иконки/приоритета. */
  readonly settlement?: boolean;
}

// Только actionable-шаги: кто действует и i18n-ключ действия. Остальные processingStep → ожидание.
const ACTION_MAP: Partial<Record<ProcessingStep, StepAction>> = {
  WAIT_SELLER_SPEC_SIGNED: { actor: 'SELLER', labelKey: 'app.home.action.signSpec' },
  WAIT_BUYER_SPEC_SIGNED: { actor: 'BUYER', labelKey: 'app.home.action.signSpec' },
  WAIT_AGENT_SPEC_SIGNED: { actor: 'AGENT', labelKey: 'app.home.action.signSpec' },
  WAIT_SELLER_SEND_SCHEDULE: { actor: 'SELLER', labelKey: 'app.home.action.sendSchedule' },
  WAIT_SELLER_CONFIRM_SCHEDULE: { actor: 'SELLER', labelKey: 'app.home.action.confirmSchedule' },
  WAIT_BUYER_CONFIRM_SCHEDULE: { actor: 'BUYER', labelKey: 'app.home.action.confirmSchedule' },
  WAIT_SHIPMENT_IS_READY_CONFIRMATION: { actor: 'SELLER', labelKey: 'app.home.action.confirmShipmentReady' },
  WAIT_CONSIGNEE_ASSIGNED: { actor: 'BUYER', labelKey: 'app.home.action.assignConsignee' },
  WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED: { actor: 'AGENT', labelKey: 'app.home.action.signForwarderInstruction' },
  WAIT_DELIVERY: { actor: 'BUYER', labelKey: 'app.home.action.confirmDelivery' },
  WAIT_ATTESTATION: { actor: 'BUYER', labelKey: 'app.home.action.attest' },
  WAIT_DECLARATION_SENDING: { actor: 'SELLER', labelKey: 'app.home.action.sendDeclaration' },
  WAIT_UPLOAD_SIGNED_UPD_TORG12: { actor: 'SELLER', labelKey: 'app.home.action.signUpd' },
  WAIT_UPLOAD_UNDERPAYMENT: { actor: 'BUYER', labelKey: 'app.home.action.payUnderpayment', settlement: true },
  WAIT_WIRE_UNDERPAYMENT_CONFIRMATION: { actor: 'SELLER', labelKey: 'app.home.action.confirmPaymentReceived', settlement: true },
  WAIT_UPLOAD_OVERPAYMENT: { actor: 'SELLER', labelKey: 'app.home.action.returnOverpayment', settlement: true },
  WAIT_RETURN_OVERPAYMENT_CONFIRMATION: { actor: 'BUYER', labelKey: 'app.home.action.confirmReturnReceived', settlement: true },
};

// Чего ждём, когда ход за другой стороной: i18n-ключ по шагу сделки. Шаг сам называет, кто действует,
// поэтому текст говорит и что ждём, и от кого. Автоматических шагов (формирование документов, обработка,
// экспедитор) здесь нет: на них не действует ни одна из сторон, и придумывать, чего ждём, нельзя.
const WAIT_MAP: Partial<Record<ProcessingStep, string>> = {
  WAIT_SELLER_SPEC_SIGNED: 'app.home.wait.specBySeller',
  WAIT_BUYER_SPEC_SIGNED: 'app.home.wait.specByBuyer',
  WAIT_AGENT_SPEC_SIGNED: 'app.home.wait.specByAgent',
  WAIT_SELLER_SEND_SCHEDULE: 'app.home.wait.scheduleFromSeller',
  WAIT_SELLER_CONFIRM_SCHEDULE: 'app.home.wait.scheduleBySeller',
  WAIT_BUYER_CONFIRM_SCHEDULE: 'app.home.wait.scheduleByBuyer',
  WAIT_SHIPMENT_IS_READY_CONFIRMATION: 'app.home.wait.shipmentReady',
  WAIT_CONSIGNEE_ASSIGNED: 'app.home.wait.consignee',
  WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED: 'app.home.wait.forwarderInstruction',
  WAIT_DELIVERY: 'app.home.wait.delivery',
  WAIT_ATTESTATION: 'app.home.wait.attestation',
  WAIT_DECLARATION_SENDING: 'app.home.wait.declaration',
  WAIT_UPLOAD_SIGNED_UPD_TORG12: 'app.home.wait.upd',
  WAIT_UPLOAD_UNDERPAYMENT: 'app.home.wait.underpayment',
  WAIT_WIRE_UNDERPAYMENT_CONFIRMATION: 'app.home.wait.underpaymentConfirm',
  WAIT_UPLOAD_OVERPAYMENT: 'app.home.wait.overpayment',
  WAIT_RETURN_OVERPAYMENT_CONFIRMATION: 'app.home.wait.overpaymentConfirm',
};

/** Чего ждём от другой стороны (i18n-ключ), либо undefined: ход мой или шаг автоматический. */
export function waitLabelFor(step: ProcessingStep | undefined, actor: Actor): string | undefined {
  if (!step) {
    return undefined;
  }
  const action = ACTION_MAP[step];
  return action && action.actor !== actor ? WAIT_MAP[step] : undefined;
}

/** Что должен сделать Я по сделке сейчас (ключ действия + settlement), либо null — не мой ход.
 *  `actor` — моя роль в сделке: продавец/покупатель (по стороне вкладки) либо агент (посредник). */
export function myActionFor(step: ProcessingStep | undefined, actor: Actor): StepAction | null {
  if (!step) {
    return null;
  }
  const action = ACTION_MAP[step];
  return action && action.actor === actor ? action : null;
}

// «Новая» = свежесозданная сделка на раннем шаге и ещё в работе. Включает первый шаг WAIT_ATTESTATION:
// с него начинается сделка, и на нём одна из сторон/агент ещё не действует — но должна видеть, что
// сделка появилась (в ленте «Требует внимания» как информационный элемент, см. buildAttention).
const EARLY_STEPS = new Set<ProcessingStep>([
  'BARGAIN_CREATED',
  'WAIT_ATTESTATION',
  'WAIT_BUYER_SPEC_SIGNED',
  'WAIT_SELLER_SPEC_SIGNED',
  'WAIT_AGENT_SPEC_SIGNED',
  'SPECIFICATION_COMPLETED',
]);
const NEW_DEAL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

export function isNewDeal(item: SearchBargain, nowMs: number): boolean {
  if (item.status !== 'IN_PROGRESS' || !item.processingStep || !EARLY_STEPS.has(item.processingStep)) {
    return false;
  }
  // createdDate приходит от бэкенда в формате «dd.MM.yyyy» (order-service DateUtils.toString). Сырой
  // new Date("04.08.2026") читает её как MM.DD → 8 апреля вместо 4 августа → сделка ложно «стара» и
  // выпадает из окна новизны. Парсим через parseDeadline (понимает и dd.MM.yyyy, и ISO).
  const created = parseDeadline(item.createdDate);
  return created !== null && nowMs - created <= NEW_DEAL_WINDOW_MS;
}

/**
 * Просрочка сделки, ещё находящейся в работе. Сигнал — `schedule.finishDelays`: авторитетная задержка
 * в днях, которую считает бэкенд (order-service `ShipmentScheduleUtils.calculateBargainFinishDelay`):
 * >0 только когда сделка реально опаздывает по отгрузке/экспедированию, 0 — когда идёт в срок или раньше.
 *
 * Важно: НЕ сравниваем `plannedFinishDate < today` вручную. `plannedFinishDate` — плановый финиш ОТГРУЗКИ
 * (середина сделки), поэтому любая нормально идущая сделка в пост-отгрузочной стадии (декларация, оплата,
 * взаиморасчёты) оказалась бы «за датой отгрузки» и ложно помечалась просроченной. `finishDelays` этой
 * ловушки лишён — он равен 0, пока нет фактического опоздания.
 */
export function isOverdue(item: SearchBargain): boolean {
  return item.status === 'IN_PROGRESS' && (item.schedule?.finishDelays ?? 0) > 0;
}

// Шаги закрытия: для покупателя и агента это ещё работа (итоговая сверка), для продавца сделка уже
// завершена — сервер относит её к его «Завершённым» (BargainService.resolveBargainStageName). Фильтр
// completed=false их не отсекает, поэтому продавцу их убираем здесь.
const SELLER_FINISHED_STEPS = new Set<ProcessingStep>(['BARGAIN_CLOSING', 'BARGAIN_CLOSED']);

/** Сделка ещё в работе для меня: статус «в работе», а продавцу — не на шагах закрытия. */
export function isActiveForMe(side: DealOrderType, item: SearchBargain, isAgent: boolean): boolean {
  if (item.status !== 'IN_PROGRESS') {
    return false;
  }
  const seller = !isAgent && side === 'SALE';
  return !(seller && item.processingStep != null && SELLER_FINISHED_STEPS.has(item.processingStep));
}

export interface DealWithSide {
  readonly side: DealOrderType;
  readonly item: SearchBargain;
}

export type AttentionKind = 'blocked' | 'overdue' | 'settlement' | 'action' | 'info';

export interface AttentionItem {
  readonly key: string;
  readonly dealId: number;
  /** Отображаемый № сделки (контракт или «№id»). */
  readonly dealNo: string;
  readonly kind: AttentionKind;
  /** Меньше — выше в списке. */
  readonly priority: number;
  /** Готовый текст (для ошибок-приостановок из уведомлений). Взаимоисключим с actionLabelKey. */
  readonly title?: string;
  /** i18n-ключ действия (для «мой ход»). */
  readonly actionLabelKey?: string;
  /** i18n-ключ «чего ждём от другой стороны» — для строк «Ждём других», если шаг за конкретной стороной. */
  readonly waitLabelKey?: string;
  readonly goods?: string;
  readonly date?: string;
  /** Чей ход: мой или ждём других. */
  readonly group: AttentionGroup;
  /** Контрагент: для моей продажи — покупатель, для закупки — продавец. У агента стороны нет. */
  readonly counterparty?: string;
  readonly weightTon?: number;
  /** Плановая сумма без НДС по роли: продавцу за товар, покупателю и агенту полная с логистикой. */
  readonly amount?: number;
  /** Опоздание в днях. */
  readonly delayDays?: number;
}

export type AttentionGroup = 'mine' | 'waiting';

/** Сколько строк показывать в каждой группе: так просрочка «ждём других» не вытесняется чужими делами. */
const MAX_PER_GROUP = 5;

function dealNoOf(item: SearchBargain): string {
  const contract = item.contractNum?.trim();
  return contract && contract.length > 0 ? contract : `№${item.id}`;
}

/** Моя роль в сделке: агент — посредник, иначе продавец или покупатель по стороне вкладки. */
export function actorOf(side: DealOrderType | undefined, isAgent: boolean): Actor {
  return isAgent ? 'AGENT' : side === 'SALE' ? 'SELLER' : 'BUYER';
}

/** Ход сделки для строки списка «Мои сделки»: номер шага, название этапа и чей ход или опоздание. */
export interface DealProgress {
  /** «1/12» — если сервер отдал номер шага; у завершённой сделки номера нет. */
  readonly stepLabel?: string;
  readonly stage?: string;
  /** Опоздание в днях: важнее «Ваш ход», поэтому строка показывает его, если оно есть. */
  readonly lateDays?: number;
  readonly mine: boolean;
}

export function dealProgressFor(side: DealOrderType | undefined, item: SearchBargain, isAgent: boolean): DealProgress {
  const stepLabel = item.stepNum != null && item.stepsCount ? `${item.stepNum}/${item.stepsCount}` : undefined;
  // Продавцу шаги закрытия уже завершены (isActiveForMe): ни «Ваш ход», ни опоздания там не пишем.
  const active = isAgent || side == null ? item.status === 'IN_PROGRESS' : isActiveForMe(side, item, isAgent);
  const lateDays = active && isOverdue(item) ? item.schedule?.finishDelays : undefined;
  const mine = active && myActionFor(item.processingStep, actorOf(side, isAgent)) != null;
  return { stepLabel, stage: item.stage, lateDays, mine };
}

/**
 * Плановая сумма сделки без НДС по роли (FR-BRG-50): продавцу — за товар (FCA), покупателю и агенту — полная
 * с логистикой (CPT). Одна функция для ленты «Главной», панели и списка «Мои сделки», чтобы суммы не расходились.
 */
export function dealAmountFor(side: DealOrderType | undefined, item: SearchBargain, isAgent: boolean): number | undefined {
  const buyerView = isAgent || side === 'PURCHASE';
  // Запасной расчёт на случай старой версии сервера: сумма не должна молча пропасть.
  const goodsAmount = item.plannedWeight != null && item.price != null ? item.plannedWeight * item.price : undefined;
  return (buyerView ? item.payment?.fullPriceCPTWithoutNds : undefined) ?? item.payment?.fullPriceFCAWithoutNds ?? goodsAmount;
}

/** Данные сделки для строки ленты: контрагент, вес и сумма по спецификации. */
function dealFacts(side: DealOrderType, item: SearchBargain, isAgent: boolean) {
  return {
    goods: item.goodsType,
    counterparty: isAgent ? undefined : (side === 'SALE' ? item.buyer : item.seller)?.name,
    weightTon: item.plannedWeight,
    amount: dealAmountFor(side, item, isAgent),
  };
}

/** Приостановки/ошибки из ленты уведомлений (messageStatus=ERROR + bargainId) → приоритет 1.
 *  По одной сделке — одна строка, самое свежее уведомление. */
export function errorAttention(notifications: readonly NotificationItem[] | undefined): AttentionItem[] {
  const out = new Map<number, AttentionItem>();
  for (const notification of notifications ?? []) {
    const bargainId = notification.payload?.bargainId;
    if (notification.payload?.messageStatus === 'ERROR' && typeof bargainId === 'number') {
      const known = out.get(bargainId);
      if (known && (parseDeadline(known.date) ?? 0) >= (parseDeadline(notification.createdDate) ?? 0)) {
        continue;
      }
      out.set(bargainId, {
        key: `err-${bargainId}`,
        dealId: bargainId,
        dealNo: `№${bargainId}`,
        kind: 'blocked',
        priority: 1,
        title: notification.payload?.messageHeader,
        date: notification.createdDate,
        group: 'mine',
      });
    }
  }
  return [...out.values()];
}

/**
 * Лента «требует внимания»: ошибки-приостановки (prio 1) + просрочки (overdue, prio 1, всем ролям) +
 * сделки, где мой ход (settlement prio 2, прочие действия prio 3) + новые сделки (info, prio 4). Одна сделка —
 * один элемент; ошибка и просрочка важнее обычного действия. Порядок — по срочности: сначала мой ход, затем
 * приоритет, при равном приоритете — свежая дата (решение пользователя 2026-11-02). Даты сравниваем численно:
 * у сделок формат «dd.MM.yyyy», строковое сравнение упорядочило бы по дню месяца. Дата: у ошибок — время
 * уведомления, у просрочек — плановый срок, у действий и новых — {@code createdDate} сделки.
 */
export function buildAttention(
  deals: readonly DealWithSide[],
  notifications: readonly NotificationItem[] | undefined,
  isAgent = false,
  nowMs = Date.now(),
  activeComplete = false,
): AttentionItem[] {
  // Строку приостановки дополняем данными сделки: в уведомлении нет ни номера, ни товара.
  const dealById = new Map(deals.map((deal) => [deal.item.id, deal]));
  // Уведомления хранятся всей историей, поэтому приостановка завершённой или отменённой сделки уже не дело.
  // «Главная» загружает только активные сделки: если выборка полная (activeComplete), сделки нет среди
  // активных — значит, она завершена или отменена. При неполной выборке это неизвестно, строку оставляем.
  const items = errorAttention(notifications).flatMap((error) => {
    const deal = dealById.get(error.dealId);
    if (!deal) {
      return activeComplete ? [] : [error];
    }
    if (!isActiveForMe(deal.side, deal.item, isAgent)) {
      return [];
    }
    return [{ ...error, dealNo: dealNoOf(deal.item), ...dealFacts(deal.side, deal.item, isAgent) }];
  });
  const seen = new Set<number>(items.map((item) => item.dealId));

  for (const { side, item } of deals) {
    if (item.id == null || seen.has(item.id) || !isActiveForMe(side, item, isAgent)) {
      continue;
    }
    // Роль в сделке: агент — посредник (actor AGENT); иначе продавец/покупатель по стороне вкладки.
    const actor = actorOf(side, isAgent);
    // Просрочка показывается ЛЮБОЙ роли, включая агента: хотя агент пассивен, опоздание по сопровождаемой
    // сделке ему важно (координирует стороны). Просрочка критична и перекрывает обычный «мой ход» по этой
    // сделке — важнее показать, что срок истёк, чем конкретное действие (оно видно на карточке сделки).
    if (isOverdue(item)) {
      seen.add(item.id);
      // Если по сделке есть мой ход — кладём его ключ: строка покажет действие и опоздание.
      // Если хода нет (жду контрагента или автоматический этап) — ключ пуст, покажем «срок сделки истёк».
      const overdueAction = myActionFor(item.processingStep, actor);
      items.push({
        key: `overdue-${item.id}`,
        dealId: item.id,
        dealNo: dealNoOf(item),
        kind: 'overdue',
        priority: 1,
        actionLabelKey: overdueAction?.labelKey,
        waitLabelKey: overdueAction ? undefined : waitLabelFor(item.processingStep, actor),
        date: item.schedule?.plannedFinishDate,
        group: overdueAction ? 'mine' : 'waiting',
        delayDays: item.schedule?.finishDelays,
        ...dealFacts(side, item, isAgent),
      });
      continue;
    }
    const action = myActionFor(item.processingStep, actor);
    if (!action) {
      // Ход сейчас не мой, но если сделка НОВАЯ (свежая, на раннем шаге) — показываем её информационным
      // элементом «новая сделка» ЛЮБОЙ роли (продавец/покупатель/агент): каждый участник должен сразу
      // видеть, что сделка появилась. Без призыва к действию и ниже приоритетом любых «моих ходов»
      // (priority 4). Строка называет, чего ждём от другой стороны, если шаг за ней.
      if (isNewDeal(item, nowMs)) {
        seen.add(item.id);
        items.push({
          key: `new-${item.id}`,
          dealId: item.id,
          dealNo: dealNoOf(item),
          kind: 'info',
          priority: 4,
          actionLabelKey: 'app.home.action.newDeal',
          waitLabelKey: waitLabelFor(item.processingStep, actor),
          date: item.createdDate,
          group: 'waiting',
          ...dealFacts(side, item, isAgent),
        });
      }
      continue;
    }
    seen.add(item.id);
    items.push({
      key: `act-${item.id}`,
      dealId: item.id,
      dealNo: dealNoOf(item),
      kind: action.settlement ? 'settlement' : 'action',
      priority: action.settlement ? 2 : 3,
      actionLabelKey: action.labelKey,
      date: item.createdDate,
      group: 'mine',
      ...dealFacts(side, item, isAgent),
    });
  }

  return items.sort(
    (a, b) =>
      Number(a.group === 'waiting') - Number(b.group === 'waiting') ||
      a.priority - b.priority ||
      (parseDeadline(b.date) ?? 0) - (parseDeadline(a.date) ?? 0),
  );
}

export interface AttentionView {
  readonly mine: readonly AttentionItem[];
  readonly waiting: readonly AttentionItem[];
  /** Всего сделок, где мой ход, включая не поместившиеся. */
  readonly mineTotal: number;
  /** Сколько строк не поместилось. */
  readonly hidden: number;
}

/** Разбивка ленты на группы с ограничением числа строк в каждой. */
export function attentionView(items: readonly AttentionItem[]): AttentionView {
  const mine = items.filter((item) => item.group === 'mine');
  const waiting = items.filter((item) => item.group === 'waiting');
  const shownMine = mine.slice(0, MAX_PER_GROUP);
  const shownWaiting = waiting.slice(0, MAX_PER_GROUP);
  return {
    mine: shownMine,
    waiting: shownWaiting,
    mineTotal: mine.length,
    hidden: items.length - shownMine.length - shownWaiting.length,
  };
}

// ── Ближайшие сроки по сделкам (плановое завершение отгрузки) ────────────────────
// Источник — schedule.plannedFinishDate из уже загруженного списка (без доп. запросов).

const RU_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

/** Дата-строка (ISO или dd.MM.yyyy) → ms, либо null. */
export function parseDeadline(value: string | undefined): number | null {
  if (!value) {
    return null;
  }
  const ru = RU_DATE.exec(value.trim());
  const ms = Date.parse(ru ? `${ru[3]}-${ru[2]}-${ru[1]}` : value);
  return Number.isNaN(ms) ? null : ms;
}

function startOfDay(ms: number): number {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;

export type DeadlineUrgency = 'today' | 'tomorrow' | 'date';

function urgencyOf(dateMs: number, nowMs: number): DeadlineUrgency {
  const days = Math.round((startOfDay(dateMs) - startOfDay(nowMs)) / DAY_MS);
  if (days <= 0) {
    return 'today';
  }
  return days === 1 ? 'tomorrow' : 'date';
}

export interface UpcomingDeadline {
  readonly dealId: number;
  readonly dealNo: string;
  readonly goods?: string;
  readonly dateMs: number;
  readonly urgency: DeadlineUrgency;
}

const DEADLINE_WINDOW_DAYS = 14;
const MAX_DEADLINES = 5;

/**
 * Предстоящие сроки (плановое завершение отгрузки) в окне DEADLINE_WINDOW_DAYS, по возрастанию.
 * Только активные сделки; прошедшие/далёкие/без даты — отбрасываем; дедуп по сделке; cap MAX_DEADLINES.
 */
export function upcomingDeadlines(
  deals: readonly DealWithSide[],
  nowMs: number,
  isAgent = false,
  withinDays: number = DEADLINE_WINDOW_DAYS,
  max: number = MAX_DEADLINES,
): UpcomingDeadline[] {
  const today = startOfDay(nowMs);
  const horizon = today + withinDays * DAY_MS;
  const seen = new Set<number>();
  const out: UpcomingDeadline[] = [];

  for (const { side, item } of deals) {
    // То же правило «активна для меня», что у панели и ленты: продавцу сделка на шагах закрытия уже завершена.
    if (item.id == null || seen.has(item.id) || !isActiveForMe(side, item, isAgent)) {
      continue;
    }
    const ms = parseDeadline(item.schedule?.plannedFinishDate);
    if (ms == null) {
      continue;
    }
    const day = startOfDay(ms);
    if (day < today || day > horizon) {
      continue;
    }
    seen.add(item.id);
    out.push({
      dealId: item.id,
      dealNo: dealNoOf(item),
      goods: item.goodsType,
      dateMs: ms,
      urgency: urgencyOf(ms, nowMs),
    });
  }

  return out.sort((a, b) => a.dateMs - b.dateMs).slice(0, max);
}

// ── Сводка активных сделок для панели «Главной» ──────────────────────────────────

export interface DealsSummary {
  /** Сколько сделок в работе для меня (без завершённых для продавца шагов закрытия). */
  readonly count: number;
  /** Сумма активных закупок без НДС: покупателю полная, с логистикой. undefined — сумма неизвестна. */
  readonly buying?: number;
  /** Сумма активных продаж без НДС за товар. */
  readonly selling?: number;
  /** Сумма всех сопровождаемых сделок у агента (полная). */
  readonly total?: number;
  /** Мой ход по шагу сделки, в том числе по опоздавшим. Приостановки из уведомлений сюда не входят: они
   *  есть в строке «N сделок ждут вашего решения», но не в раскладке шагов. */
  readonly mine: number;
  /** Срок сделки истёк, чей бы ни был ход. */
  readonly late: number;
  /** Опоздавшие сделки, где ход мой: входят и в mine, и в late. */
  readonly mineLate: number;
  /** Ход за другими, срок не истёк. */
  readonly others: number;
}

/**
 * Сводка панели «Мои сделки» по загруженным активным сделкам: суммы по роли и раскладка «ждут вас /
 * опаздывают / ждут контрагента». «Ждут вас» и «опаздывают» пересекаются (mineLate): опоздавшая сделка с моим
 * ходом попадает в обе подписи, как в строке «N сделок ждут вашего решения» и в ленте. Сумма стороны неизвестна (undefined), если у хотя бы одной сделки нет суммы: неполную сумму не
 * показываем. Стороны без сделок дают undefined, и строка не выводится.
 */
export function dealsSummary(deals: readonly DealWithSide[], isAgent: boolean): DealsSummary {
  let count = 0;
  let mine = 0;
  let late = 0;
  let mineLate = 0;
  let others = 0;
  const sums: Record<'buying' | 'selling' | 'total', { sum: number; count: number; known: boolean }> = {
    buying: { sum: 0, count: 0, known: true },
    selling: { sum: 0, count: 0, known: true },
    total: { sum: 0, count: 0, known: true },
  };

  for (const { side, item } of deals) {
    if (!isActiveForMe(side, item, isAgent)) {
      continue;
    }
    count += 1;
    const actor = actorOf(side, isAgent);
    const overdue = isOverdue(item);
    const myMove = myActionFor(item.processingStep, actor) != null;
    late += overdue ? 1 : 0;
    mine += myMove ? 1 : 0;
    mineLate += overdue && myMove ? 1 : 0;
    others += !overdue && !myMove ? 1 : 0;
    const bucket = sums[isAgent ? 'total' : side === 'SALE' ? 'selling' : 'buying'];
    const { amount } = dealFacts(side, item, isAgent);
    // Итог покупателя и агента — с логистикой: сумма за товар, подставленная строке ленты без суммы CPT, занизила бы
    // «Покупаю на», поэтому такая сделка делает итог неизвестным, а не точным.
    const withoutLogistics = (isAgent || side === 'PURCHASE') && item.payment?.fullPriceCPTWithoutNds == null;
    bucket.count += 1;
    if (amount == null || !Number.isFinite(amount) || withoutLogistics) {
      bucket.known = false;
    } else {
      bucket.sum += amount;
    }
  }

  const value = (key: keyof typeof sums): number | undefined =>
    sums[key].count > 0 && sums[key].known ? sums[key].sum : undefined;
  return { count, buying: value('buying'), selling: value('selling'), total: value('total'), mine, late, mineLate, others };
}
